"use client";

import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";

import { MobileNav } from "@/components";
import { NAV_ITEMS, SERVICE_LINKS } from "@/constants";

function isActive(pathname: string, href: string): boolean {
  if (href === "/") return pathname === "/";
  return pathname === href || pathname.startsWith(`${href}/`);
}

export default function Header() {
  const pathname = usePathname() || "/";
  const servicesActive = isActive(pathname, "/services");

  return (
    <header className="sticky top-0 z-40 border-b border-[var(--stroke)] bg-[var(--bg)] md:bg-[var(--bg)]/90 md:backdrop-blur-lg">
      <div className="mx-auto flex w-full max-w-7xl items-center justify-between gap-4 px-4 py-2 sm:px-6">
        {/* Brand */}
        <Link href="/" className="flex items-center" aria-label="Cybitix home">
          <Image
            src="/images/cybitix-solution-logo.png"
            alt="Cybitix"
            width={95}
            height={95}
            priority
            className="h-12 w-auto sm:h-14"
          />
        </Link>

        {/* Desktop nav */}
        <nav
          aria-label="Primary"
          className="hidden items-center gap-8 text-sm font-medium text-[var(--muted)] md:flex"
        >
          {/* Home rendered first so it isn't lumped with the Services dropdown */}
          <Link
            href="/"
            className={`nav-link transition hover:text-[var(--text)] ${
              isActive(pathname, "/") ? "nav-link-active" : ""
            }`}
          >
            Home
          </Link>

          <div className="group relative">
            <Link
              href="/services"
              className={`nav-link inline-flex items-center gap-1 transition hover:text-[var(--text)] ${
                servicesActive ? "nav-link-active" : ""
              }`}
            >
              Services
            </Link>
            <div className="pointer-events-none absolute left-0 top-8 z-40 w-72 rounded-lg border border-[var(--stroke)] bg-[var(--panel)]/95 p-2 opacity-0 backdrop-blur transition duration-200 group-hover:pointer-events-auto group-hover:opacity-100">
              {SERVICE_LINKS.map((service) => (
                <Link
                  key={service.slug}
                  href={`/services/${service.slug}`}
                  className="block rounded-lg px-4 py-2.5 text-sm text-[var(--text)] transition hover:bg-[var(--accent-soft)]"
                >
                  {service.label}
                </Link>
              ))}
              <div className="my-1 border-t border-[var(--stroke)]" />
              <Link
                href="/services"
                className="group/more flex items-center justify-between rounded-lg px-4 py-2.5 text-sm font-semibold text-[var(--accent)] transition hover:bg-[var(--accent-soft)]"
              >
                <span>More services</span>
                <span aria-hidden className="transition-transform duration-200 group-hover/more:translate-x-1">→</span>
              </Link>
            </div>
          </div>

          {NAV_ITEMS.filter((n) => n.href !== "/").map((n) => (
            <Link
              key={n.href}
              href={n.href}
              className={`nav-link transition hover:text-[var(--text)] ${
                isActive(pathname, n.href) ? "nav-link-active" : ""
              }`}
            >
              {n.label}
            </Link>
          ))}
        </nav>

        {/* Right-side cluster: desktop CTA + mobile hamburger */}
        <div className="flex items-center gap-3">
          <Link
            href="/contact"
            className="hidden sm:inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[var(--accent)] to-[var(--accent-2)] px-5 py-2.5 text-sm font-semibold text-[#07111f] shadow-[0_0_24px_-6px_var(--accent)] transition hover:brightness-110"
          >
            Let&apos;s Talk
          </Link>
          <MobileNav />
        </div>
      </div>
    </header>
  );
}
